import type {
  Appearance,
  CustomFontSource,
  Stripe,
  StripeElementBase,
  StripeElements,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { CHECKOUT_OUTCOME_NOTICE } from '../lib/checkout-outcome'
import { useTRPC } from '../lib/trpc'
import './checkout-elements.css'

const TOKEN_NAMES = [
  '--font-ui',
  '--text-base',
  '--text-sm',
  '--text-xs',
  '--weight-body',
  '--weight-label',
  '--s-1',
  '--s-2',
  '--s-3',
  '--highlight',
  '--text-heading',
  '--surface-raised',
  '--text-primary',
  '--text-secondary',
  '--text-muted',
  '--danger',
  '--border-default',
] as const

export type CheckoutToken = (typeof TOKEN_NAMES)[number]

/**
 * The interface face and its Hebrew fallback, handed to Stripe's frame by URL.
 *
 * The frame is another document and cannot see our `@font-face` rules, so
 * without these the interface stack names faces it does not have and the field
 * sets in the system face. Stripe fetches them from its own origin, which is
 * why the server answers font assets with that origin allowed.
 */
async function interfaceFonts(
  interfaceFamily: string,
  hebrewFamily: string,
): Promise<CustomFontSource[]> {
  const { fontUrl } = await import('../lib/font-bytes')
  return [
    {
      family: interfaceFamily,
      file: 'Inter-Variable.woff2',
      weight: '100 900',
    },
    { family: hebrewFamily, file: 'AdanimSans-Text.woff2', weight: '400' },
    { family: hebrewFamily, file: 'AdanimSans-Text.woff2', weight: '450' },
    { family: hebrewFamily, file: 'AdanimSans-Medium.ttf', weight: '500' },
  ].flatMap(({ family, file, weight }) => {
    if (!family) return []
    const url = fontUrl(file)
    return url
      ? [
          {
            family,
            src: `url(${new URL(url, window.location.origin).href})`,
            weight,
            display: 'swap',
          },
        ]
      : []
  })
}

export function checkoutAppearance(
  token: (name: CheckoutToken) => string,
): Appearance {
  return {
    theme: 'flat',
    labels: 'above',
    variables: {
      fontFamily: token('--font-ui'),
      fontSizeBase: token('--text-base'),
      fontSizeSm: token('--text-sm'),
      fontSizeXs: token('--text-xs'),
      fontWeightNormal: token('--weight-body'),
      fontWeightMedium: token('--weight-label'),
      /* Stripe derives every inset from this one unit, so it takes the scale's
         base step: a larger one inflates each field and tab together. */
      spacingUnit: token('--s-1'),
      gridRowSpacing: token('--s-3'),
      colorPrimary: token('--text-heading'),
      colorBackground: token('--surface-raised'),
      colorText: token('--text-primary'),
      colorTextSecondary: token('--text-secondary'),
      colorTextPlaceholder: token('--text-muted'),
      colorDanger: token('--danger'),
      inputColorBorder: token('--border-default'),
      inputFocusColorBorder: token('--highlight'),
      /* A tab's icon is colored by its own variables, so the selected one
         follows the selected label into ink rather than staying the light mark
         Stripe draws for a filled tab. */
      colorIconTab: token('--text-secondary'),
      colorIconTabSelected: token('--text-heading'),
      colorIconTabHover: token('--text-heading'),
      focusOutline: `1.5px solid ${token('--highlight')}`,
      borderRadius: '0',
      buttonBorderRadius: '0',
      buttonExpressCheckoutBorderRadius: '0',
    },
    rules: {
      /* The same inset our own fields carry, so a card sits at the height of
         the name above it rather than at whatever the frame's defaults give. */
      '.Input': {
        border: `1px solid ${token('--border-default')}`,
        boxShadow: 'none',
        padding: `${token('--s-2')} ${token('--s-3')}`,
      },
      '.Input:focus': {
        borderColor: token('--highlight'),
        boxShadow: 'none',
      },
      '.Tab': {
        border: `1px solid ${token('--border-default')}`,
        boxShadow: 'none',
        padding: `${token('--s-2')} ${token('--s-3')}`,
      },
      /* Selected is an ink border and an ink label on the page's own surface.
         The primary color would fill the whole tab, which is a block of ink
         where this system marks a choice with a line and a word. */
      '.Tab--selected': {
        backgroundColor: token('--surface-raised'),
        borderColor: token('--text-heading'),
        color: token('--text-heading'),
        boxShadow: 'none',
      },
      '.TabLabel--selected': { color: token('--text-heading') },
      '.TabIcon--selected': { color: token('--text-heading') },
    },
  }
}

export type CheckoutConfirm = () => Promise<void>

export type CheckoutHandshakeState = {
  locked: boolean
  completed: boolean
  pending: boolean
  generation: number
}

export type CheckoutHandshakeEvent =
  | 'checkout_opened'
  | 'reopened'
  | { type: 'confirm_started'; generation: number }
  | { type: 'confirm_settled'; generation: number }
  | { type: 'completed'; generation: number }

export function checkoutHandshakeReducer(
  state: CheckoutHandshakeState,
  event: CheckoutHandshakeEvent,
): CheckoutHandshakeState {
  if (event === 'reopened') {
    return {
      locked: false,
      completed: false,
      pending: state.pending,
      generation: state.generation + 1,
    }
  }
  if (event === 'checkout_opened') {
    return state.locked ? state : { ...state, locked: true, completed: false }
  }
  if (event.type === 'confirm_started') {
    return event.generation === state.generation
      ? { ...state, pending: true }
      : state
  }
  if (event.type === 'confirm_settled') {
    return { ...state, pending: false }
  }
  return event.generation === state.generation
    ? { ...state, locked: true, completed: true }
    : state
}

/**
 * The intent a client secret belongs to, refused for anything that is not a
 * PaymentIntent or a SetupIntent: the card fields confirm nothing else, and a
 * Checkout session's secret is never mounted here.
 */
export function intentOf(clientSecret: string): 'payment' | 'setup' {
  if (clientSecret.startsWith('seti_')) return 'setup'
  if (clientSecret.startsWith('pi_')) return 'payment'
  throw new Error('That is not a payment the card fields can confirm.')
}

/**
 * Why a confirmed intent is not done, or null when it is.
 *
 * Processing counts as done: the bank has the payment and the webhook records
 * it when it clears. Anything still asking for a card or an action is open,
 * and a canceled one has been closed — by Back, or by its window passing.
 */
export function intentCompletionError(status: string): string | null {
  if (
    status === 'succeeded' ||
    status === 'processing' ||
    status === 'requires_capture'
  ) {
    return null
  }
  return status === 'canceled'
    ? CHECKOUT_OUTCOME_NOTICE.expired
    : 'The payment is still open. Check the payment details and try again.'
}

/** The query keys a confirmed checkout leaves on the address. */
const RETURN_KEYS = [
  'checkout',
  'payment_intent',
  'payment_intent_client_secret',
  'setup_intent',
  'setup_intent_client_secret',
  'redirect_status',
  'session_id',
] as const

/**
 * The checkout a page is coming back to, read off its address: ours, written
 * when the card fields confirmed and on the address a redirect returns to;
 * else the intent Stripe appends; else a Checkout session's.
 */
export function returnedCheckoutId(params: URLSearchParams): string | null {
  return (
    params.get('checkout') ??
    params.get('payment_intent') ??
    params.get('setup_intent') ??
    params.get('session_id')
  )
}

/** The address without what a confirmed checkout left on it. */
export function withoutReturnedCheckout(href: string): URL {
  const url = new URL(href)
  for (const key of RETURN_KEYS) url.searchParams.delete(key)
  return url
}

/**
 * The address showing a checkout's outcome, as a path the history takes:
 * only the checkout's id is kept, so the client secret and the redirect's
 * status Stripe appended do not stay in the bar, the history or a copied
 * link. Null takes the checkout off.
 */
export function addressShowing(
  href: string,
  checkoutId: string | null,
): string {
  const url = withoutReturnedCheckout(href)
  if (checkoutId) url.searchParams.set('checkout', checkoutId)
  return `${url.pathname}${url.search}${url.hash}`
}

/** Moves the address on a page no router owns, keeping the entry's state. */
function replaceAddress(path: string): void {
  history.replaceState(history.state, '', path)
}

/**
 * Name the checkout a page is showing the outcome of on its address, so a
 * reload comes back to it; null takes it off. Called from an event, never
 * while rendering.
 */
export function showReturnedCheckout(checkoutId: string | null): void {
  replaceAddress(addressShowing(window.location.href, checkoutId))
}

/**
 * The checkout a page comes back to, and the way to show another or none.
 *
 * Read off the address as the page first renders, and the address tidied in
 * an effect once it has: rendering reads and never writes. `replace` moves
 * the address — the browser's history on a page no router owns, or the
 * router's own where one does, so the router's search state agrees with the
 * bar.
 */
export function useReturnedCheckout(
  replace: (path: string) => void = replaceAddress,
): [string | null, (checkoutId: string | null) => void] {
  const [checkoutId, setCheckoutId] = useState(() =>
    returnedCheckoutId(new URLSearchParams(window.location.search)),
  )
  const replaceRef = useRef(replace)
  replaceRef.current = replace
  const arrivedWith = useRef(checkoutId)
  useEffect(() => {
    if (arrivedWith.current) {
      replaceRef.current(
        addressShowing(window.location.href, arrivedWith.current),
      )
    }
  }, [])
  const show = useCallback((next: string | null) => {
    setCheckoutId(next)
    replaceRef.current(addressShowing(window.location.href, next))
  }, [])
  return [checkoutId, show]
}

/**
 * Run a confirmation unless one is already in flight, and say whether it ran.
 * A refused start is its caller's to answer: a wallet's sheet waits on an
 * answer, and one never given leaves it spinning.
 */
export async function runAlone(
  pending: { current: boolean },
  run: () => Promise<void>,
  around: { started: () => void; settled: () => void },
): Promise<boolean> {
  if (pending.current) return false
  pending.current = true
  around.started()
  try {
    await run()
  } finally {
    pending.current = false
    around.settled()
  }
  return true
}

/**
 * A wallet's confirmation, held as in flight where the page holds them, and
 * failed at once where another is in flight already.
 */
export async function confirmWallet(
  track: ((run: () => Promise<void>) => Promise<boolean>) | undefined,
  run: () => Promise<void>,
  refuse: () => void,
): Promise<void> {
  if (!track) {
    await run()
    return
  }
  if (!(await track(run))) refuse()
}

export function useCheckoutConfirmation({
  onComplete,
  writeCheckoutToUrl = false,
}: {
  onComplete: (checkoutId: string) => void
  writeCheckoutToUrl?: boolean
}) {
  const [confirm, setConfirm] = useState<CheckoutConfirm | null>(null)
  const [state, dispatch] = useReducer(checkoutHandshakeReducer, {
    locked: false,
    completed: false,
    pending: false,
    generation: 0,
  })
  const onCompleteRef = useRef(onComplete)
  const pendingRef = useRef(false)
  onCompleteRef.current = onComplete
  pendingRef.current = state.pending

  const complete = useCallback(
    (checkoutId: string) => {
      dispatch({ type: 'completed', generation: state.generation })
      if (writeCheckoutToUrl) showReturnedCheckout(checkoutId)
      onCompleteRef.current(checkoutId)
    },
    [state.generation, writeCheckoutToUrl],
  )

  const reset = useCallback(() => {
    setConfirm(null)
    dispatch('reopened')
  }, [])

  /* One in flight at a time, whichever way it was started: the typed card
     through our own commit, or a wallet through its own button in the frame.
     Both hold the checkout while the bank answers, so nothing that would let
     it go — Back, or a second Pay — is offered until it has. */
  const generation = state.generation
  const track = useCallback(
    (run: () => Promise<void>) =>
      runAlone(pendingRef, run, {
        started: () => dispatch({ type: 'confirm_started', generation }),
        settled: () => dispatch({ type: 'confirm_settled', generation }),
      }),
    [generation],
  )

  return {
    confirm:
      state.completed || !confirm
        ? null
        : async () => {
            await track(confirm)
          },
    track,
    completed: state.completed,
    locked: state.locked,
    pending: state.pending,
    onConfirmAvailable: useCallback((next: CheckoutConfirm | null) => {
      setConfirm(() => next)
      if (next) dispatch('checkout_opened')
    }, []),
    onComplete: complete,
    reset,
  }
}

/**
 * What the card fields are drawn from before any intent exists, as the server
 * prepared it from the answers: the checkout the submit names, the figure and
 * mode the fields must agree with, and what they offer.
 */
export type CardStart = {
  checkoutId: string
  mode: 'payment' | 'setup'
  amountCents: number | null
  keepCard: boolean
  /** Apple Pay and Google Pay are drawn. */
  wallets: boolean
  /** Link is offered. */
  link: boolean
}

/**
 * Stripe's card fields over a checkout the server prepared, confirmed from
 * our own commit.
 *
 * **No intent exists until the payer submits.** The fields are drawn from the
 * prepared figure and mode alone; the submit asks the server for the intent,
 * which checks again that what the checkout holds — a dedication's unit, a
 * pledge, an office link — may still be paid, and only then creates it. A
 * refusal is said here, in the payer's words, and nothing is charged.
 *
 * Stripe draws no email field: the page asks what it needs in its own fields,
 * and Stripe is given no address to mail a receipt to. The billing address the
 * bank checks is the Payment Element's own to ask, by the card's country, and a
 * wallet carries its own. A card that needs the bank's authentication (3DS) is
 * answered in Stripe's own dialog while the confirmation waits; a method that
 * sends the payer away brings them back to this address, naming the checkout.
 *
 * Apple Pay and Google Pay sit in the express element above the card wherever
 * the checkout offers them — never at the office's desk, where they would be
 * the treasurer's. Link is offered in the card fields on a public page only.
 */
export function CheckoutElements({
  orgId,
  spaceId,
  card,
  onConfirmAvailable,
  onComplete,
  trackConfirm,
  onCardChange,
  onConfirmingChange,
}: {
  orgId: string
  /** The office's space: the card fields are the desk's. */
  spaceId?: string
  card: CardStart
  onConfirmAvailable: (confirm: CheckoutConfirm | null) => void
  onComplete: (checkoutId: string) => void
  /**
   * Holds a wallet's confirmation as in flight, the way Pay holds the card's,
   * and says whether it ran: false while another is in flight.
   */
  trackConfirm?: (run: () => Promise<void>) => Promise<boolean>
  onCardChange?: () => void
  onConfirmingChange?: (confirming: boolean) => void
}) {
  const trpc = useTRPC()
  const publicConfig = useQuery({
    ...trpc.donation.checkoutConfig.queryOptions({ shortId: orgId }),
    enabled: !spaceId,
  })
  const officeConfig = useQuery({
    ...trpc.billing.checkoutConfig.queryOptions({ spaceId: spaceId ?? orgId }),
    enabled: Boolean(spaceId),
  })
  const config = spaceId ? officeConfig : publicConfig
  const startPublic = useMutation(trpc.donation.startCheckout.mutationOptions())
  const startOffice = useMutation(trpc.billing.startCheckout.mutationOptions())
  /* The submit's own request: the office's, signed in, or a public page's,
     whose bearer is the checkout's id. */
  const start = () =>
    spaceId
      ? startOffice.mutateAsync({ spaceId, checkoutId: card.checkoutId })
      : startPublic.mutateAsync({ shortId: orgId, checkoutId: card.checkoutId })
  const startRef = useRef(start)
  startRef.current = start
  const onConfirmAvailableRef = useRef(onConfirmAvailable)
  const onCompleteRef = useRef(onComplete)
  const trackRef = useRef(trackConfirm)
  const onCardChangeRef = useRef(onCardChange)
  const onConfirmingChangeRef = useRef(onConfirmingChange)
  trackRef.current = trackConfirm
  onCardChangeRef.current = onCardChange
  onConfirmingChangeRef.current = onConfirmingChange
  onConfirmAvailableRef.current = onConfirmAvailable
  onCompleteRef.current = onComplete
  const rootRef = useRef<HTMLDivElement>(null)
  const expressRef = useRef<HTMLDivElement>(null)
  const paymentRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [mounted, setMounted] = useState(false)
  const { checkoutId, mode, amountCents, keepCard, wallets, link } = card

  useEffect(() => {
    if (!config.data || !rootRef.current || !paymentRef.current) return
    let active = true
    let confirming = false
    let completed = false
    let elements: StripeElements | null = null
    const mountedElements: StripeElementBase[] = []
    const root = rootRef.current
    const completeCheckout = onCompleteRef.current
    const readAppearance = () => {
      const style = getComputedStyle(root)
      return checkoutAppearance((name) => style.getPropertyValue(name).trim())
    }
    setError(null)
    setMounted(false)
    onConfirmAvailableRef.current(null)

    const finish = async (
      stripe: Stripe,
      group: StripeElements,
      expressCheckoutConfirmEvent?: StripeExpressCheckoutElementConfirmEvent,
    ) => {
      const failed = (message: string) => {
        if (active) setError(message)
        expressCheckoutConfirmEvent?.paymentFailed({ reason: 'fail', message })
      }
      /* One confirmation at a time. A wallet tapped while another is in
         flight, or after the payment went through, is told so at once, or
         its sheet would wait on an answer that never comes. */
      if (confirming || completed) {
        expressCheckoutConfirmEvent?.paymentFailed({ reason: 'fail' })
        return
      }
      confirming = true
      onConfirmingChangeRef.current?.(true)
      setError(null)
      try {
        /* The fields are checked before anything is asked of the server, so
           an unfinished card is said where it is rather than refused by the
           bank. A deferred confirmation must submit first, wallets too. */
        const submitted = await group.submit()
        if (submitted.error) {
          failed(submitted.error.message ?? 'Check the card details.')
          return
        }
        let clientSecret: string
        try {
          clientSecret = (await startRef.current()).clientSecret
        } catch (err) {
          failed(
            err instanceof Error && err.message
              ? err.message
              : 'The payment could not be started. Please try again.',
          )
          return
        }
        /* The fields were drawn for one mode and confirm only an intent of
           that kind: a payment's fields never confirm a card kept, or the
           reverse. */
        if (intentOf(clientSecret) !== mode) {
          failed(
            'This payment does not match the form. Go back and continue again.',
          )
          return
        }
        const returnTo = withoutReturnedCheckout(window.location.href)
        returnTo.searchParams.set('checkout', checkoutId)
        const confirmParams = { return_url: returnTo.href }
        const result =
          mode === 'setup'
            ? await stripe.confirmSetup({
                elements: group,
                clientSecret,
                confirmParams,
                redirect: 'if_required',
              })
            : await stripe.confirmPayment({
                elements: group,
                clientSecret,
                confirmParams,
                redirect: 'if_required',
              })
        if (result.error) {
          failed(
            result.error.message ??
              'The card was not accepted. Check the details and try again.',
          )
          return
        }
        const intent =
          'setupIntent' in result ? result.setupIntent : result.paymentIntent
        const incomplete = intentCompletionError(intent.status)
        if (incomplete) {
          failed(incomplete)
          return
        }
        completed = true
        completeCheckout(checkoutId)
      } catch (err) {
        console.error('The Stripe confirmation threw.', err)
        failed(
          'The payment could not be confirmed. Please check your connection and try again.',
        )
      } finally {
        if (!completed) {
          confirming = false
          onConfirmingChangeRef.current?.(false)
        }
      }
    }

    const mount = async () => {
      try {
        const { loadStripe } = await import('@stripe/stripe-js')
        const stripe = await loadStripe(config.data.publishableKey, {
          ...(config.data.stripeAccountId
            ? { stripeAccount: config.data.stripeAccountId }
            : {}),
        })
        if (!stripe) throw new Error('Stripe.js did not load.')
        const style = getComputedStyle(root)
        const family = (name: string) =>
          style
            .getPropertyValue(name)
            .trim()
            .split(',')[0]
            ?.trim()
            .replace(/^['"]|['"]$/g, '')
        /* What the fields agree with is what the server will create: the
           mode, the figure, whether the card is kept, and the methods. */
        const shared = {
          currency: 'usd',
          paymentMethodTypes: link ? ['card', 'link'] : ['card'],
          appearance: readAppearance(),
          fonts: await interfaceFonts(
            family('--font-ui') ?? '',
            family('--family-sans'),
          ),
        }
        const group =
          mode === 'setup'
            ? stripe.elements({ ...shared, mode: 'setup' })
            : stripe.elements({
                ...shared,
                mode: 'payment',
                amount: amountCents ?? 0,
                ...(keepCard ? { setupFutureUsage: 'off_session' } : {}),
              })
        elements = group
        if (!active || !paymentRef.current) return

        if (wallets && expressRef.current) {
          /* Apple Pay and Google Pay, and nothing else: Link, where it is
             offered, is in the card fields below, and no method that sends
             the payer elsewhere is taken. */
          const express = group.create('expressCheckout', {
            paymentMethods: {
              link: 'never',
              paypal: 'never',
              amazonPay: 'never',
              klarna: 'never',
            },
          })
          express.on('ready', ({ availablePaymentMethods }) => {
            if (active && expressRef.current) {
              expressRef.current.hidden = !availablePaymentMethods
            }
          })
          express.on('confirm', (event) => {
            void confirmWallet(
              trackRef.current,
              () => finish(stripe, group, event),
              () => event.paymentFailed({ reason: 'fail' }),
            )
          })
          express.mount(expressRef.current)
          mountedElements.push(express)
        } else if (expressRef.current) {
          expressRef.current.hidden = true
        }
        /* One strip of methods, which is the shape the appearance rules above
           style and the shape a reader already knows from a tab strip. Every
           wallet sits in the express element above, so this draws none of
           them a second time. */
        const payment = group.create('payment', {
          layout: 'tabs',
          wallets: {
            applePay: 'never',
            googlePay: 'never',
            link: link ? 'auto' : 'never',
          },
        })
        /* An error from the last submit describes the form as it was. Once
           the payer changes it — verifies with Link, picks a card — it no
           longer does, and a stale error over a valid form reads as a
           refusal. */
        payment.on('change', () => {
          if (active) setError(null)
          onCardChangeRef.current?.()
        })
        payment.mount(paymentRef.current)
        mountedElements.push(payment)
        onConfirmAvailableRef.current(() => finish(stripe, group))
        setMounted(true)
      } catch (err) {
        /* The sentence a donor reads says what to do and nothing about Stripe.
           What actually refused is logged, because a payment field that fails
           silently can only be diagnosed by deploying again to watch it. */
        console.error('The Stripe payment element did not mount.', err)
        if (active) {
          setError(
            'The secure payment form could not be loaded. Please try again.',
          )
        }
      }
    }

    void mount()
    const updateAppearance = () =>
      elements?.update({ appearance: readAppearance() })
    const observer = new MutationObserver(updateAppearance)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })
    const colorScheme = matchMedia('(prefers-color-scheme: dark)')
    colorScheme.addEventListener('change', updateAppearance)
    return () => {
      active = false
      observer.disconnect()
      colorScheme.removeEventListener('change', updateAppearance)
      onConfirmAvailableRef.current(null)
      for (const element of mountedElements) element.destroy()
    }
  }, [config.data, checkoutId, mode, amountCents, keepCard, wallets, link])

  if (config.error) {
    return (
      <p className="checkout-elements__error" role="alert">
        The secure payment form could not be loaded.
      </p>
    )
  }

  return (
    <div ref={rootRef} className="checkout-elements">
      {error ? (
        <p className="checkout-elements__error" role="alert">
          {error}
        </p>
      ) : null}
      {!mounted && !error ? <p>Loading secure payment…</p> : null}
      {/* Laid out from the start, because a wallet button mounted into a box
          with no width draws itself at that width and never grows. It is the
          ready event that takes it away where no wallet is offered. */}
      <div ref={expressRef} className="checkout-elements__express" />
      <div ref={paymentRef} className="checkout-elements__payment" />
    </div>
  )
}
